import { STATIC_PLANS } from '../data/staticPlans';
import type { FreeCreditSchedule, PlanConfig } from '../services/subscriptionService';

import { getPlanBillingInterval } from './pricingPlans';

export interface PlanCreditSummary {
  unlimited: boolean;
  signupOnly: boolean;
  annual: boolean;
  upfront: number;
  daily: number;
  dailyGrantDays: number;
  total: number;
  /** Free plan only: a larger daily grant for the first `boostDays` days of each monthly cycle. */
  boostDaily?: number;
  boostDays?: number;
  expiresEachCycle?: boolean;
}

const nonNegative = (amount: number): number => Number.isFinite(amount) ? Math.max(0, amount) : 0;
const roundCredits = (amount: number): number => Math.round(amount * 100) / 100;

const freeSchedule = (plan: PlanConfig): FreeCreditSchedule | undefined => (
  plan.free_credit_schedule ?? STATIC_PLANS.free?.free_credit_schedule
);

const freeSummary = (plan: PlanConfig, schedule: FreeCreditSchedule): PlanCreditSummary => {
  const upfront = nonNegative(schedule.upfront ?? plan.monthly_credits);
  const tailDays = Math.max(0, schedule.cycle_days - schedule.boost_days);

  return {
    unlimited: false, signupOnly: true, annual: false, upfront,
    daily: nonNegative(schedule.daily), dailyGrantDays: tailDays,
    boostDaily: nonNegative(schedule.boost_daily), boostDays: schedule.boost_days,
    expiresEachCycle: schedule.expires_each_cycle,
    total: roundCredits(upfront + schedule.boost_daily * schedule.boost_days + schedule.daily * tailDays),
  };
};

const paidSummary = (planKey: string, plan: PlanConfig, isAnnual: boolean): PlanCreditSummary => {
  const unlimited = plan.is_unlimited === true || planKey === 'unlimited';
  const annual = planKey !== 'free' && getPlanBillingInterval(planKey, isAnnual) === 'annual';
  const upfront = nonNegative(plan.monthly_credits) * (annual ? 12 : 1);
  const daily = planKey === 'free' || unlimited ? 0 : nonNegative(
    plan.daily_drip_credits ?? STATIC_PLANS[planKey]?.daily_drip_credits ?? 0,
  );
  const dailyGrantDays = annual ? 365 : 30;

  return {
    unlimited, signupOnly: planKey === 'free', annual, upfront, daily, dailyGrantDays,
    total: roundCredits(upfront + daily * dailyGrantDays),
  };
};

export const getPlanCreditSummary = (
  planKey: string, plan: PlanConfig, isAnnual: boolean,
): PlanCreditSummary | null => {
  if (planKey === 'enterprise' || planKey === 'byok') return null;
  const schedule = planKey === 'free' ? freeSchedule(plan) : undefined;

  return schedule ? freeSummary(plan, schedule) : paidSummary(planKey, plan, isAnnual);
};

export const formatPlanCredits = (amount: number): string => amount.toLocaleString('en-US', {
  maximumFractionDigits: 2,
});

/** "10 / day for 10 days, then 1 / day" for the free cycle; "15 / day" otherwise. */
export const formatDailyCredits = (summary: PlanCreditSummary): string => {
  if (summary.boostDaily && summary.boostDays) {
    return `${formatPlanCredits(summary.boostDaily)} / day for ${summary.boostDays} days, then `
      + `${formatPlanCredits(summary.daily)} / day`;
  }

  return summary.daily > 0 ? `${formatPlanCredits(summary.daily)} / day` : 'None';
};

export const isCreditAllowanceHighlight = (text: string): boolean => (
  /^(?:[\d,.]+ Credits|\+[\d,.]+ Bonus Credits|Unlimited Credits)/i.test(text)
);
